import { type BillingPeriod, MAX_CHILDREN, type PlanId, planById } from './catalogue'

/**
 * Draft persistence for the checkout form.
 *
 * sessionStorage on purpose, not localStorage: it survives the two scenarios
 * that actually lose people — a refresh, and navigating around the marketing
 * site mid-checkout — but is wiped when the tab closes, so children's names
 * never linger on a shared family computer.
 *
 * The draft is deliberately NOT cleared on handoff: bouncing off the Shopify
 * page with the Back button must restore the filled form, because "went to
 * Shopify, changed their mind about the plan" is a real round trip. It ends
 * when a purchase completes, which the shop reports through a cookie (see
 * purchase.ts).
 */

/** Versioned: change the suffix when the shape changes and old drafts are dropped. */
export const DRAFT_KEY = 'cv.checkout.v2'

export interface CheckoutDraft {
  step: 'plan' | 'details'
  billing: BillingPeriod
  planId: PlanId
  childNames: string[]
}

/**
 * Validate an untrusted blob into a draft, or reject it entirely.
 *
 * sessionStorage content is outside our control (stale versions of this
 * bundle, other page scripts), so hydration must never crash a mount or
 * resurrect an invalid state — a rejected draft just means default state.
 */
function toDraft(value: unknown): CheckoutDraft | null {
  if (!value || typeof value !== 'object') return null
  const draft = value as Record<string, unknown>

  if (draft.step !== 'plan' && draft.step !== 'details') return null
  if (draft.billing !== 'annual' && draft.billing !== 'monthly') return null
  if (typeof draft.planId !== 'string' || !planById(draft.planId)) return null
  if (!Array.isArray(draft.childNames)) return null

  const childNames = draft.childNames
    .filter((name): name is string => typeof name === 'string')
    .slice(0, MAX_CHILDREN)
  if (childNames.length === 0) childNames.push('')

  return {
    step: draft.step,
    billing: draft.billing,
    planId: draft.planId as PlanId,
    childNames,
  }
}

export function loadDraft(): CheckoutDraft | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY)
    if (!raw) return null
    return toDraft(JSON.parse(raw))
  } catch {
    // Storage access throws in private-mode Safari and when storage is
    // blocked; malformed JSON throws too. A lost draft is harmless.
    return null
  }
}

export function saveDraft(draft: CheckoutDraft): void {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
  } catch {
    // Never let persistence break the form itself.
  }
}

/** Forget the draft: a purchase completed, and there is nothing left to restore. */
export function clearDraft(): void {
  try {
    sessionStorage.removeItem(DRAFT_KEY)
  } catch {
    // Unreachable storage held no draft to keep.
  }
}
